import React from 'react';
import {
  SuccessNotiPositionEnum,
  ShowSuccessNotificationType,
} from '@components/resident/commons';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { FileListData } from '@typings/ResidentTypes';
import {
  AdvanceDecisonType,
  CprDecisionType,
  AcmpType,
  successMessage,
} from '../../utils';
import HasAdvanceDecision from './components/HasAdvanceDecision';
import AdrtUpdatedDate from './components/AdrtUpdatedDate';
import AdrtLocation from './components/AdrtLocation';
import CprDecision from './components/CprDecision';
import CprDetails from './components/CprDetails';
import CprLocation from './components/CprLocation';
import HasAcmp from './components/HasAcmp';
import AcmpUpdatedDate from './components/AcmpUpdatedDate';
import AcmpLocation from './components/AcmpLocation';
import UploadCprDocument from './components/UploadCprDocument';

enum StatusValues {
  Yes = 'yes',
  No = 'no',
  Notsure = 'notSure',
}

interface LegalCarePlanningFormsProps {
  userId: string;
  residentFullName: string;
  advanceDecision: AdvanceDecisonType;
  setAdvanceDecision: React.Dispatch<React.SetStateAction<AdvanceDecisonType>>;
  cprDecision: CprDecisionType;
  setCprDecision: React.Dispatch<React.SetStateAction<CprDecisionType>>;
  acmp: AcmpType;
  setAcmp: React.Dispatch<React.SetStateAction<AcmpType>>;
  onSaveInLegalCarePlanning: (
    e: any,
    position: SuccessNotiPositionEnum
  ) => void;
  showSuccessNotification: ShowSuccessNotificationType;
}

const LegalCarePlanningForms: React.FC<LegalCarePlanningFormsProps> = ({
  userId,
  residentFullName,
  advanceDecision,
  setAdvanceDecision,
  cprDecision,
  setCprDecision,
  acmp,
  setAcmp,
  onSaveInLegalCarePlanning,
  showSuccessNotification,
}) => {
  const uploadedCprDocumentList = cprDecision?.cprDocuments ?? [];

  const setUploadedCprDocumentList: React.Dispatch<
    React.SetStateAction<FileListData[]>
  > = nextValue => {
    setCprDecision(prev => {
      const currentFiles = prev.cprDocuments ?? [];

      const nextFiles =
        typeof nextValue === 'function'
          ? (nextValue as (prevState: FileListData[]) => FileListData[])(
              currentFiles
            )
          : nextValue;

      return {
        ...prev,
        cprDocuments: nextFiles,
      };
    });
  };
  const handleSave = (position: SuccessNotiPositionEnum) => {
    return (
      <>
        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          disabled={showSuccessNotification.btnLoading}
          onClick={e => onSaveInLegalCarePlanning(e, position)}
        >
          Save and continue
        </button>
        {showSuccessNotification.status &&
          showSuccessNotification.position === position && (
            <SuccessNotificationBanner
              title={successMessage.title}
              content={successMessage.content}
              duration={3000}
              noScrollToTop
            />
          )}
      </>
    );
  };

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-xl second-title">
        Legal Care Planning forms
      </h1>
      <div id="legal-care-planing-forms-hint" className="govuk-hint">
        When legally binding care planning forms have been completed by / with{' '}
        {residentFullName} please log the relevant details below.
      </div>
      <div className="govuk-warning-text">
        <span className="govuk-warning-text__icon" aria-hidden="true">
          !
        </span>
        <strong className="govuk-warning-text__text">
          <span className="govuk-visually-hidden">Warning</span>
          To view the legal document and the medicalised wishes they contain
          please refer to the person&apos;s latest documentation.
        </strong>
      </div>

      <HasAdvanceDecision
        advanceDecision={advanceDecision}
        setAdvanceDecision={setAdvanceDecision}
      />
      {advanceDecision.status === StatusValues.Yes && (
        <div>
          <AdrtUpdatedDate
            advanceDecision={advanceDecision}
            setAdvanceDecision={setAdvanceDecision}
          />
          <AdrtLocation
            advanceDecision={advanceDecision}
            setAdvanceDecision={setAdvanceDecision}
          />
          {handleSave(SuccessNotiPositionEnum.THIRD)}
        </div>
      )}
      <div className="mt-7 govuk-bp:mt-11">
        <h1 className="govuk-heading-l">
          Cardio-pulmonary resuscitation (CPR) decision
        </h1>
        <div className="govuk-body">
          Whether a decision has been made, the decision, who made the decision,
          the date of decision, date for review and location of documentation.
          Where the person or their family member/carer have not been informed
          of the clinical decision please state the reason why.
        </div>
      </div>
      <div
        className={`${advanceDecision.status === StatusValues.Yes ? 'mt-2 govuk-bp:mt-5' : 'mt-10 govuk-bp:mt-14'} `}
      >
        <CprDecision
          cprDecision={cprDecision}
          setCprDecision={setCprDecision}
        />
        {cprDecision.status !== '' && cprDecision.status !== undefined && (
          <div>
            <CprDetails
              cprDecision={cprDecision}
              setCprDecision={setCprDecision}
            />
            <CprLocation
              cprDecision={cprDecision}
              setCprDecision={setCprDecision}
            />
            <UploadCprDocument
              userId={userId}
              uploadedCprDocumentList={uploadedCprDocumentList}
              setUploadedCprDocumentList={setUploadedCprDocumentList}
            />
          </div>
        )}
      </div>
      <div
        className={`${acmp.status === StatusValues.Yes ? 'mb-2 govuk-bp:mb-5' : 'mb-10 govuk-bp:mb-14'} `}
      >
        <HasAcmp acmp={acmp} setAcmp={setAcmp} />
        {acmp.status === StatusValues.Yes && (
          <div>
            <AcmpUpdatedDate acmp={acmp} setAcmp={setAcmp} />
            <AcmpLocation acmp={acmp} setAcmp={setAcmp} />
            {handleSave(SuccessNotiPositionEnum.FOURTH)}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalCarePlanningForms;
